
import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useSession } from "@supabase/auth-helpers-react";
import { PrayerRequestForm } from "@/components/prayer/PrayerRequestForm";
import { PrayerRequestList } from "@/components/prayer/PrayerRequestList";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PrayerRequestsTabProps {
  showDialog?: boolean;
  setShowDialog?: (show: boolean) => void;
}

export function PrayerRequestsTab({ showDialog = false, setShowDialog }: PrayerRequestsTabProps) {
  const [showPrayerDialog, setShowPrayerDialog] = useState(showDialog);
  const [prayerRequests, setPrayerRequests] = useState<any[]>([]);
  const [comments, setComments] = useState<Record<number, any[]>>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const session = useSession();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    setShowPrayerDialog(showDialog);
  }, [showDialog]);

  const handleDialogChange = (open: boolean) => {
    setShowPrayerDialog(open);
    if (setShowDialog) {
      setShowDialog(open);
    }
  };

  const fetchPrayerRequests = async () => {
    try {
      const { data: requests, error } = await supabase
        .from('prayer_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPrayerRequests(requests || []);

      requests?.forEach(async (request) => {
        const { data: requestComments, error: commentsError } = await supabase
          .from('prayer_comments')
          .select('*')
          .eq('prayer_request_id', request.id)
          .order('created_at', { ascending: true });

        if (!commentsError && requestComments) {
          setComments(prev => ({
            ...prev,
            [request.id]: requestComments
          }));
        }
      });
    } catch (error) {
      console.error('Error fetching prayer requests:', error);
      toast({
        title: "Error",
        description: "Failed to fetch prayer requests. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchPrayerRequests();
  }, []);

  const handleDeleteRequest = async (requestId: number) => {
    try {
      const { error } = await supabase
        .from('prayer_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Prayer request deleted successfully.",
      });
      fetchPrayerRequests();
    } catch (error) {
      console.error('Error deleting prayer request:', error);
      toast({
        title: "Error",
        description: "Failed to delete prayer request.",
        variant: "destructive",
      });
    }
  };

  // For non-dialog display (on the main page), render directly
  if (!showDialog) {
    return (
      <div className="py-8 space-y-8 max-w-4xl mx-auto">
        <div className="space-y-8">
          {session && (
            <div className="bg-gradient-to-br from-white via-white to-grace-light/5 border border-grace/10 rounded-2xl p-6 shadow-lg">
              <PrayerRequestForm onSubmit={fetchPrayerRequests} />
            </div>
          )}

          <PrayerRequestList
            prayerRequests={prayerRequests}
            comments={comments}
            isAdmin={isAdmin}
            onDeleteRequest={handleDeleteRequest}
            onCommentAdded={fetchPrayerRequests}
          />
        </div>
      </div>
    );
  }

  // For dialog display
  return (
    <Dialog open={showPrayerDialog} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-[600px] bg-gradient-to-br from-white to-grace-light/10 border-grace/20 text-gray-900 max-h-screen h-screen p-0 m-0 rounded-none w-full overflow-hidden">
        <ScrollArea className="h-full max-h-screen">
          <div className="p-6 flex flex-col h-full">
            <div className="flex items-center mb-8 pt-2">
              <button 
                onClick={() => handleDialogChange(false)}
                className="mr-4 p-2 text-gray-400 hover:text-grace hover:bg-grace/10 rounded-full transition-all duration-200"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-grace to-grace-dark bg-clip-text text-transparent">
                  Prayer Requests
                </h2>
                <p className="text-gray-600 text-sm mt-1">Share and pray together</p>
              </div>
            </div>

            <div className="flex-1 space-y-6">
              {session && (
                <div className="bg-gradient-to-br from-white via-white to-grace-light/5 border border-grace/10 rounded-xl p-4 shadow-sm">
                  <PrayerRequestForm onSubmit={fetchPrayerRequests} />
                </div>
              )}

              <PrayerRequestList
                prayerRequests={prayerRequests}
                comments={comments}
                isAdmin={isAdmin}
                onDeleteRequest={handleDeleteRequest}
                onCommentAdded={fetchPrayerRequests}
              />
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
